import { oc } from "@orpc/contract";
import { z } from "zod";
import { BLOCK_OPS_MAX, BlockDocumentSchema, BlockOpSchema } from "../blocks/schema.js";
import { CANONICAL_MEMORY_ID, MEMORY_PATH_MAX_BYTES, MEMORY_SCOPE_SLUG, MEMORY_TEXT_MAX_BYTES, } from "../memory.js";
import { CANONICAL_FILE_ID } from "../outcome.js";
import { AGENT_YAML_MAX_BYTES, OVERLAY_YAML_MAX_BYTES, ProjectRevisionSchema, ProjectSidecarSchema, TemplateUpgradePreviewSchema, TemplateUpgradeStatusSchema, UpgradeChoicesSchema, } from "../projects.js";
import { ExplorerEntrySchema, GoalHelpRequestSchema, GoalHelpResponseSchema, MemoryDetailSchema, MemoryEntrySchema, MemoryScopeSchema, ProjectSchema, } from "../wire.js";
import { ProjectValidateInputSchema, ProjectValidationSchema } from "./validate.js";
const memoryRef = z.object({
    projectId: z.string().uuid(),
    memoryId: z.string().regex(CANONICAL_MEMORY_ID),
});
// Projects: a member's copies of the catalog's documents (agent.yaml, the Foundry overlay,
// the sidecar) with append-only revisions. Every procedure needs a signed-in viewer and
// answers only for the viewer's own projects on the request's site.
export const projects = {
    create: oc
        .route({
        summary: "Create a project",
        description: "A new project for the viewer, copying the named catalog version's agent.yaml, overlay, sidecar and starter files into revision 1 (or an empty starter when no version is named); the template itself is never edited. A named version retired since its page loaded copies its template's live version instead, unless exactVersion is true: then the named version itself is copied, retired or not, as long as its template is live. A template that is gone (archived, its library archived or not the platform's, or nothing of it live) is 404 template_not_found either way, as is a version never published on its template's home site. A team's members are pinned at their live versions. The answer's project.templateVersionId names the version copied.",
    })
        .meta({ access: "write" })
        .input(z.object({
        name: z.string().min(1).max(160).optional(),
        templateVersionId: z.string().uuid().optional(),
        exactVersion: z
            .boolean()
            .optional()
            .describe("true copies the named templateVersionId itself even when it is retired, as long as its template is live; false or absent copies the template's live version when the named one is retired. Without templateVersionId it changes nothing."),
    }))
        .output(z.object({ project: ProjectSchema, revision: ProjectRevisionSchema })),
    list: oc
        .route({
        summary: "The viewer's projects",
        description: "Newest first, archived ones excluded.",
    })
        .meta({ access: "read" })
        .input(z.object({}))
        .output(z.object({ projects: z.array(ProjectSchema) })),
    get: oc
        .route({
        summary: "One project and its current revision",
        description: "The project row and its latest revision; another member's project is not_found.",
    })
        .meta({ access: "read" })
        .input(z.object({ projectId: z.string().uuid() }))
        .output(z.object({ project: ProjectSchema, revision: ProjectRevisionSchema })),
    saveDraft: oc
        .route({
        summary: "Save a draft as a new revision",
        description: "Appends a revision holding the given agent.yaml, overlay and sidecar. baseRevision must be the project's current revision (409 draft_conflict otherwise). agent.yaml must parse as a Claude Managed Agents agent object (422 agent_yaml_invalid). An overlay sent must be what a start parses: a YAML mapping of the overlay schema (422 overlay_invalid {path, problem}; one left out is the prior one, kept unread). agent.yaml's MCP region must be well formed: every mcp_servers entry an https URL with no userinfo, referenced by exactly one mcp_toolset, and every toolset naming a declared server (422 mcp_server_invalid {problem, rule, server, path}; a server switched off keeps its toolset with default_config.enabled: false). A multiagent roster passes the check publish runs, each member resolved as a run resolves it: 422 under the publish diagnostic's code {reason, path} for an authored version (roster_version_unsupported), a member nothing resolves (roster_agent_unknown), a member with its own roster (roster_depth), a member pinning another inference_geo than the agent (roster_geo_mismatch) or a member named anthropic.advisor beside an enabled advisor (roster_reserved_name). The members the saved roster names are pinned, on every project: a member already pinned keeps its version, a newly named one is pinned at its latest live version, one no longer named loses its pin.",
    })
        .meta({ access: "write" })
        .input(z.object({
        projectId: z.string().uuid(),
        baseRevision: z.number().int().positive(),
        agentYaml: z.string().min(1).max(AGENT_YAML_MAX_BYTES),
        overlayYaml: z.string().max(OVERLAY_YAML_MAX_BYTES).nullable().optional(),
        sidecar: ProjectSidecarSchema.optional(),
    }))
        .output(z.object({ project: ProjectSchema, revision: ProjectRevisionSchema })),
    blocks: oc
        .route({
        summary: "A revision as blocks",
        description: "The named revision (or the current one) of the viewer's project, read through the contracts block codec (@mfa/v5-contracts/blocks, readBlocks): the root agent's blocks, every roster member as a read-only agent (readOnlyReason member) at the version the project pins (pinned {id, version}) or else at its latest live version, as a run resolves it; the project's goal, outcome and runtime blocks; the top-level keys no block models, kept as written; and counts of the project's live uploads and memory scopes. A shape the codec does not write yet reads as a block with readOnlyReason unmodelled_shape, never dropped. Another member's project is 404 project_not_found; a revision the project never had is 404 revision_not_found {currentRevision}.",
    })
        .meta({ access: "read" })
        .input(z.object({
        projectId: z.string().uuid(),
        revision: z.number().int().positive().optional(),
    }))
        .output(z.object({ document: BlockDocumentSchema })),
    applyBlocks: oc
        .route({
        summary: "Save edit operations as a new revision",
        description: "Applies the operations, in order, to the current revision's documents in place (comments, key order and unknown keys kept; @mfa/v5-contracts/blocks, applyBlockOps) and appends the result as a new revision through the same gate projects.saveDraft runs, then answers the project, the new revision and that revision as blocks. baseRevision must be the project's current revision (409 draft_conflict {currentRevision}, nothing applied). The first refused operation is 422 block_op_invalid {index, reason} with reason member_read_only, unknown_block, invalid_value, limit_exceeded, held or unmodelled_shape, and nothing is written. The team operations (setTeam, addMember, removeMember, setAdvisor, setList) edit the multiagent block in the variant it has (a new block is multiagent_20261001; changing the variant, and a coordinator's advisor or lists, are Code-view edits), and each must leave a block the contracts schema accepts (invalid_value otherwise); the members the saved roster names are pinned as saveDraft pins them. A gate refusal is saveDraft's own 422 (agent_yaml_invalid, overlay_invalid, mcp_server_invalid, tool_name_reserved, the roster_* codes, roster_advisor_pairing) with block added where the error's path names one (overlay_invalid names runtime). The runtime block edits the overlay (set runtime {runner, memoryScopes}; setSeat writes models.<id>.openrouter, a catalog id served on OpenRouter that this deployment takes a run on, else invalid_value with the served ids in data.served; a Foundry seat is held); a project with no overlay gets the smallest one a start accepts.",
    })
        .meta({ access: "write" })
        .input(z.object({
        projectId: z.string().uuid(),
        baseRevision: z.number().int().positive(),
        ops: z.array(BlockOpSchema).min(1).max(BLOCK_OPS_MAX),
    }))
        .output(z.object({
        project: ProjectSchema,
        revision: ProjectRevisionSchema,
        document: BlockDocumentSchema,
    })),
    validate: oc
        .route({
        summary: "Check a candidate on every provider",
        description: "What saving and then starting the candidate would check, for the viewer's project, without saving or starting: nothing is written, nothing is spent and no provider is called. The candidate is the base revision (baseRevision, else the current one; the answer names it, never a 409) after the edit operations given (candidate.ops, applied as projects.applyBlocks would; the first refused operation is 422 block_op_invalid {index, reason}), or the documents given (candidate.agentYaml, overlayYaml, sidecar; a missing one is the base's), or the base itself. documents is the draft gate's answer, each refusal as projects.saveDraft would answer it ({code, params}) with the block its path names. results is one entry per provider and runner (RUNTIME_PROVIDERS × ENGINE_TARGETS, narrowed by providers and runner), the candidate's models put on that provider: the launch blockers a start would answer (never mission_required), the engine's own checks (the findings, each naming its block and the builtin, server or tool it is about), the agents and profiles, what only a start can tell, and on the service runner the runner rule's eligibility. A pair this deployment does not take a run on (Foundry's are held) has engineInstalled false, a null verdict and an unchecked reason. needs is what a run of the candidate needs. A project you cannot see is 404 project_not_found; a revision it never had is 404 revision_not_found {currentRevision}. Spends the preflight window (60 a minute with runs.preflight) and, as a read, the read window.",
    })
        .meta({ access: "read" })
        .input(ProjectValidateInputSchema)
        .output(ProjectValidationSchema),
    goalHelp: oc
        .route({
        summary: "Help phrase the goal",
        description: "A rough idea (or none) in; a mission, a rubric and the fitting grader ids out, drafted by one model call against the project's own system prompt and tools. 503 goal_help_unavailable when no model is installed.",
    })
        .meta({ access: "write" })
        .input(GoalHelpRequestSchema.extend({ projectId: z.string().uuid() }))
        .output(GoalHelpResponseSchema),
    archive: oc
        .route({
        summary: "Archive a project",
        description: "Hides it from the list; runs stay readable.",
    })
        .meta({ access: "write" })
        .input(z.object({ projectId: z.string().uuid() }))
        .output(z.object({ project: ProjectSchema })),
    listFiles: oc
        .route({
        summary: "A project's uploads",
        description: "The files uploaded to this project, newest first. Each mounts read-only at /mnt/session/uploads/<name> when a run starts, on either engine.",
    })
        .meta({ access: "read" })
        .input(z.object({ projectId: z.string().uuid() }))
        .output(z.object({ files: z.array(ExplorerEntrySchema) })),
    deleteFile: oc
        .route({
        summary: "Delete an upload",
        description: "Removes the file from the project; runs already started keep their copy.",
    })
        .meta({ access: "write" })
        .input(z.object({ projectId: z.string().uuid(), fileId: z.string().regex(CANONICAL_FILE_ID) }))
        .output(z.object({ deleted: z.literal(true) })),
    listMemory: oc
        .route({
        summary: "A project's memory notes",
        description: "The notes this project keeps across runs, by path. Live notes mount read-write under /mnt/memory/project-memory/<path> on every Claude run and answer memory_read on the other engines; archived ones are listed only with includeArchived and reach no run.",
    })
        .meta({ access: "read" })
        .input(z.object({ projectId: z.string().uuid(), includeArchived: z.boolean().optional() }))
        .output(z.object({ memories: z.array(MemoryEntrySchema) })),
    getMemory: oc
        .route({
        summary: "One memory note",
        description: "The note's full text and its content sha256 (the token an update may expect).",
    })
        .meta({ access: "read" })
        .input(memoryRef)
        .output(z.object({ memory: MemoryDetailSchema })),
    createMemory: oc
        .route({
        summary: "Write a memory note",
        description: "A new note at a relative path (no leading slash, no `..`, up to 512 bytes) holding up to 100 kB of text. 422 memory_path_invalid {reason} for a path outside the rule, 409 memory_path_exists when a note (live or archived) already has it, 409 memory_path_conflict when it would sit inside or over another note's path, 422 memory_too_large and 422 too_many_memories at the limits.",
    })
        .meta({ access: "write" })
        .input(z.object({
        projectId: z.string().uuid(),
        path: z.string().min(1).max(MEMORY_PATH_MAX_BYTES),
        content: z.string().min(1).max(MEMORY_TEXT_MAX_BYTES),
    }))
        .output(z.object({ memory: MemoryEntrySchema })),
    updateMemory: oc
        .route({
        summary: "Replace a memory note's text",
        description: "With expectedSha256, the note must still hold that content (409 memory_changed {currentSha256} otherwise). An archived note is 409 memory_archived: restore it first.",
    })
        .meta({ access: "write" })
        .input(memoryRef.extend({
        content: z.string().min(1).max(MEMORY_TEXT_MAX_BYTES),
        expectedSha256: z
            .string()
            .regex(/^[0-9a-f]{64}$/)
            .optional(),
    }))
        .output(z.object({ memory: MemoryEntrySchema })),
    archiveMemory: oc
        .route({
        summary: "Archive a memory note",
        description: "Hides the note from every run (it leaves the Claude mount at the next start) without losing it; archiving twice is a no-op.",
    })
        .meta({ access: "write" })
        .input(memoryRef)
        .output(z.object({ memory: MemoryEntrySchema })),
    restoreMemory: oc
        .route({
        summary: "Restore an archived memory note",
        description: "The note is live again and mounts on the next run; restoring a live note is a no-op.",
    })
        .meta({ access: "write" })
        .input(memoryRef)
        .output(z.object({ memory: MemoryEntrySchema })),
    listMemoryScopes: oc
        .route({
        summary: "A project's memory scopes",
        description: "The named scopes the project's sessions share memory in, by slug: `project-memory` (every project has it, and a session that names no scope joins it) first, then the owner's. A template's overlay (`memory.scopes`) or a run's start input (`memoryScopes`) names the scopes a session joins; each mounts at /mnt/memory/<slug>/.",
    })
        .meta({ access: "read" })
        .input(z.object({ projectId: z.string().uuid() }))
        .output(z.object({ scopes: z.array(MemoryScopeSchema) })),
    createMemoryScope: oc
        .route({
        summary: "Add a memory scope",
        description: "A new named scope in the project (for one customer or one workstream, say): lowercase letters, digits and hyphens, up to 63 characters. 409 memory_scope_exists when the project has it; 422 too_many_memory_scopes past 50.",
    })
        .meta({ access: "write" })
        .input(z.object({
        projectId: z.string().uuid(),
        slug: z.string().regex(MEMORY_SCOPE_SLUG),
        description: z.string().max(1024).optional(),
    }))
        .output(z.object({ scope: MemoryScopeSchema })),
    templateUpgrade: oc
        .route({
        summary: "Is a newer template version available?",
        description: "The version the project was created from and the template's latest published version; upgradeAvailable when the latest would change the project (an agent.yaml section, the overlay, the goal or the outcome differs, or it ships a new starter file). A version that changes only the template's placement or copy offers no upgrade. A blank project has neither.",
    })
        .meta({ access: "read" })
        .input(z.object({ projectId: z.string().uuid() }))
        .output(z.object({ upgrade: TemplateUpgradeStatusSchema })),
    templateUpgradePreview: oc
        .route({
        summary: "Preview a template upgrade",
        description: "The three-way merge of the project's current documents against the version it was created from and the target, per section: auto (the target applies), keep (the owner's edit stays) or conflict (a choice is needed). The target must be a published version of the same template.",
    })
        .meta({ access: "read" })
        .input(z.object({ projectId: z.string().uuid(), targetVersionId: z.string().uuid() }))
        .output(z.object({ preview: TemplateUpgradePreviewSchema })),
    templateUpgradeApply: oc
        .route({
        summary: "Apply a template upgrade",
        description: "Appends a revision holding the merged documents (conflicts resolved by choices: current | target per section) and pins the project to the target version. baseRevision must be the project's current revision (409 draft_conflict); an unresolved conflict is 422 upgrade_conflicts, and merged documents whose MCP region is not well formed are 422 mcp_server_invalid, as on a draft save (tools from one side and mcp_servers from the other can strand a server); a merged roster that fails the draft save's roster check is 422 under that check's code.",
    })
        .meta({ access: "write" })
        .input(z.object({
        projectId: z.string().uuid(),
        targetVersionId: z.string().uuid(),
        baseRevision: z.number().int().positive(),
        choices: UpgradeChoicesSchema.default({}),
    }))
        .output(z.object({
        upgraded: z.literal(true),
        project: ProjectSchema,
        revision: ProjectRevisionSchema,
    })),
};
//# sourceMappingURL=projects.js.map